/**
 * AdminForms — list view for the native Form Builder (Aug 2026).
 * Lists every form template (any status) with publish/unpublish/
 * archive/duplicate actions, mirroring AdminArticles.jsx conventions.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Copy, Eye, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../../components/ui/alert-dialog";

const STATUS_STYLES = {
  draft: "bg-slate-200 text-ink",
  published: "bg-green-100 text-green-800",
  unpublished: "bg-amber-100 text-amber-800",
  archived: "bg-slate-100 text-ink-soft",
};

export default function AdminForms() {
  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/forms");
      setTemplates(data.templates || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load forms.");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  const runAction = async (templateId, action) => {
    setBusyId(templateId);
    try {
      if (action === "duplicate") {
        const { data } = await api.post(`/api/admin/forms/${templateId}/duplicate`);
        toast.success("Template duplicated as a new draft.");
        setTemplates((prev) => [...prev, data]);
      } else {
        await api.post(`/api/admin/forms/${templateId}/${action}`);
        toast.success(`Template ${action}ed.`);
        await load();
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const deleteTemplate = async (templateId) => {
    setBusyId(templateId);
    try {
      await api.delete(`/api/admin/forms/${templateId}`);
      toast.success("Form template permanently deleted.");
      setTemplates((prev) => prev.filter((t) => t.template_id !== templateId));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not delete this template.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-forms-page">
      <PageHero
        eyebrow="JCC Admin"
        title="Form Builder"
        description="Create and publish interactive, printable JCC forms — assign them to a membership tier, no coding or Google Drive needed."
      >
        <div className="flex items-center gap-2">
          <Link to="/admin" className="btn-outline text-sm" data-testid="admin-forms-back-home">
            <ArrowLeft className="w-4 h-4" /> Admin home
          </Link>
          <Link to="/admin/forms/new" className="btn-teal text-sm" data-testid="admin-forms-new-btn">
            <Plus className="w-4 h-4" /> New form
          </Link>
        </div>
      </PageHero>

      <section className="section pt-8 pb-24">
        <div className="container-jcc max-w-5xl">
          {!loaded ? (
            <p className="text-ink-muted text-sm py-10 text-center" data-testid="admin-forms-loading">Loading forms…</p>
          ) : templates.length === 0 ? (
            <p className="text-ink-muted text-sm py-10 text-center border border-dashed border-slate-200 rounded-xl" data-testid="admin-forms-empty">
              No form templates yet — create your first one.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="admin-forms-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-ink-muted">
                  <tr>
                    <th className="p-3">Title</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Entitlements</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Order</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.template_id} className="border-t border-slate-100" data-testid={`admin-forms-row-${t.template_id}`}>
                      <td className="p-3 font-medium text-ink max-w-xs truncate">{t.title}</td>
                      <td className="p-3 text-ink-muted">{t.category || "—"}</td>
                      <td className="p-3 text-ink-muted text-xs">{(t.entitlements || []).join(", ") || "—"}</td>
                      <td className="p-3">
                        <span className={`pill text-xs font-semibold ${STATUS_STYLES[t.status] || ""}`} data-testid={`admin-forms-status-${t.template_id}`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="p-3 text-ink-muted">{t.display_order}</td>
                      <td className="p-3 text-right whitespace-nowrap space-x-1">
                        <Link to={`/admin/forms/${t.template_id}/edit`} className="inline-flex items-center gap-1 text-teal font-medium mr-2" data-testid={`admin-forms-edit-${t.template_id}`}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </Link>
                        {t.status === "published" ? (
                          <button disabled={busyId === t.template_id} onClick={() => runAction(t.template_id, "unpublish")}
                                  className="text-amber-700 font-medium mr-2" data-testid={`admin-forms-unpublish-${t.template_id}`}>
                            Unpublish
                          </button>
                        ) : t.status !== "archived" && (
                          <button disabled={busyId === t.template_id} onClick={() => runAction(t.template_id, "publish")}
                                  className="text-green-700 font-medium mr-2" data-testid={`admin-forms-publish-${t.template_id}`}>
                            Publish
                          </button>
                        )}
                        {t.status === "archived" ? (
                          <button disabled={busyId === t.template_id} onClick={() => runAction(t.template_id, "unarchive")}
                                  className="text-ink-muted font-medium mr-2" data-testid={`admin-forms-unarchive-${t.template_id}`}>
                            Restore
                          </button>
                        ) : t.published_at ? (
                          <button disabled={busyId === t.template_id} onClick={() => runAction(t.template_id, "archive")}
                                  className="text-ink-muted font-medium mr-2" data-testid={`admin-forms-archive-${t.template_id}`}>
                            Archive
                          </button>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                disabled={busyId === t.template_id}
                                className="text-red-600 hover:text-red-700 font-medium mr-2 inline-flex items-center gap-1"
                                data-testid={`admin-forms-delete-${t.template_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`admin-forms-delete-dialog-${t.template_id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Permanently delete this form template?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  You are about to permanently delete <strong>{t.title}</strong>. This template has
                                  never been published, so no member has ever filled it in — deleting it cannot be
                                  undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`admin-forms-delete-cancel-${t.template_id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteTemplate(t.template_id)}
                                  className="bg-red-600 hover:bg-red-700 focus:ring-red-400"
                                  data-testid={`admin-forms-delete-confirm-${t.template_id}`}
                                >
                                  Delete permanently
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        <button disabled={busyId === t.template_id} onClick={() => runAction(t.template_id, "duplicate")}
                                className="inline-flex items-center gap-1 text-ink-muted font-medium" data-testid={`admin-forms-duplicate-${t.template_id}`}>
                          <Copy className="w-3.5 h-3.5" /> Duplicate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
